import type {
    ResponsibleRule,
    ResponsibleRuleFilter,
    SensitiveRule,
    SensitiveRuleFilter
} from '@idea2app/ai-work-order-service';
import { Icon } from 'idea-react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Column, Field, RestTable } from 'mobx-restful-table';
import { Component } from 'react';
import { Badge, Button, Card, Container } from 'react-bootstrap';
import { RouteComponentProps, withRouter } from 'react-router-class-tools';

import {
    responsibleRuleStore,
    RuleType,
    sensitiveRuleStore
} from '../model/Rule';
import { i18n, I18nKey, t } from '../model/Translation';

const RuleTypeLabels: Record<string, I18nKey> = {
    work_order_chain: 'work_order_chain',
    keyword_semantic: 'keyword_semantic'
};

const WarningLevelColors: Record<number, string> = {
    1: 'warning',
    2: 'warning',
    3: 'danger'
};

const WarningLevelLabels: Record<number, I18nKey> = {
    1: 'level_1_warning',
    2: 'level_2_warning',
    3: 'level_3_warning'
};

interface RuleListParams {
    type: RuleType;
}

@withRouter
@observer
export class RuleListPage extends Component<
    RouteComponentProps<RuleListParams>
> {
    get isSensitive() {
        return this.props.match.params.type === 'sensitive';
    }

    get pageTitle() {
        return this.isSensitive
            ? t('sensitive_rule_list')
            : t('responsible_rule_list');
    }

    get pageIcon() {
        return this.isSensitive ? 'shield-exclamation' : 'shield-check';
    }

    @computed
    get sensitiveFilterFields(): Field<SensitiveRuleFilter>[] {
        return [
            { key: 'keywords', renderLabel: t('search') },
            { key: 'vocabularyName', renderLabel: t('vocabulary_name') },
            {
                key: 'ruleType',
                type: 'select',
                renderLabel: t('rule_type'),
                options: [
                    { value: '', label: t('all_statuses') },
                    {
                        value: 'work_order_chain',
                        label: t('work_order_chain')
                    },
                    {
                        value: 'keyword_semantic',
                        label: t('keyword_semantic')
                    }
                ]
            }
        ];
    }

    @computed
    get responsibleFilterFields(): Field<ResponsibleRuleFilter>[] {
        return [
            { key: 'keywords', renderLabel: t('search') },
            { key: 'ruleName', renderLabel: t('rule_name') },
            { key: 'ruleDescription', renderLabel: t('rule_description') }
        ];
    }

    @computed
    get sensitiveColumns(): Column<SensitiveRule>[] {
        return [
            {
                key: 'vocabularyName',
                renderHead: t('vocabulary_name')
            },
            {
                key: 'vocabularyDescription',
                renderHead: t('vocabulary_description')
            },
            {
                key: 'ruleType',
                renderHead: t('rule_type'),
                renderBody: ({ ruleType }) =>
                    t(RuleTypeLabels[ruleType as string])
            },
            {
                key: 'warningLevel',
                renderHead: t('warning_level'),
                renderBody: ({ warningLevel }) => (
                    <Badge bg={WarningLevelColors[warningLevel]}>
                        {t(WarningLevelLabels[warningLevel])}
                    </Badge>
                )
            },
            {
                key: 'triggeredWorkOrderCount',
                type: 'number',
                renderHead: t('triggered_work_order_count'),
                renderBody: ({ triggeredWorkOrderCount }) =>
                    triggeredWorkOrderCount || 0
            },
            {
                renderHead: t('operation'),
                renderBody: () => (
                    <div className="d-flex gap-2 justify-content-center">
                        <Button variant="link" size="sm" className="p-0">
                            <Icon name="pencil" />
                        </Button>
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-danger"
                        >
                            <Icon name="trash" />
                        </Button>
                    </div>
                )
            }
        ];
    }

    @computed
    get responsibleColumns(): Column<ResponsibleRule>[] {
        return [
            {
                key: 'ruleName',
                renderHead: t('rule_name')
            },
            {
                key: 'ruleDescription',
                renderHead: t('rule_description')
            },
            {
                key: 'associatedDocuments',
                renderHead: t('associated_documents'),
                renderBody: ({ associatedDocuments }) =>
                    associatedDocuments
                        ?.map(({ fileName }) => fileName)
                        .join('、') || '-'
            },
            {
                renderHead: t('operation'),
                renderBody: () => (
                    <div className="d-flex gap-2 justify-content-center">
                        <Button variant="link" size="sm" className="p-0">
                            <Icon name="pencil" />
                        </Button>
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-danger"
                        >
                            <Icon name="trash" />
                        </Button>
                    </div>
                )
            }
        ];
    }

    render() {
        return (
            <Card className="m-3" body>
                <div className="mb-3 d-flex justify-content-between align-items-center">
                    <h4>
                        <Icon name={this.pageIcon} className="me-2" />
                        {this.pageTitle}
                    </h4>
                </div>
                {this.isSensitive ? (
                    <RestTable
                        filterFields={this.sensitiveFilterFields}
                        columns={this.sensitiveColumns}
                        store={sensitiveRuleStore}
                        translator={i18n}
                        striped
                        hover
                        editable
                        deletable
                    />
                ) : (
                    <RestTable
                        filterFields={this.responsibleFilterFields}
                        columns={this.responsibleColumns}
                        store={responsibleRuleStore}
                        translator={i18n}
                        striped
                        hover
                        editable
                        deletable
                    />
                )}
            </Card>
        );
    }
}
